import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Euro, Search, Upload, Eye, FileText, User, Calendar, Clock,
  CheckCircle, AlertCircle, Loader2, Download, Users, Briefcase,
  HandCoins, ThumbsUp, Star, FileCheck, XCircle, ChevronRight,
  Building2, CreditCard, Receipt
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Payment request status
const STATUS_CONFIG = {
  pending_validation: { label: 'En attente validation', color: 'bg-amber-100 text-amber-700', icon: Clock },
  agency_validated: { label: 'Validé agence', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  client_validated: { label: 'Validé client', color: 'bg-emerald-100 text-emerald-700', icon: ThumbsUp },
  survey_completed: { label: 'Enquête complétée', color: 'bg-purple-100 text-purple-700', icon: Star },
  ready_for_payment: { label: 'Prêt pour paiement', color: 'bg-green-100 text-green-700', icon: Euro },
  invoice_uploaded: { label: 'Facture déposée', color: 'bg-cyan-100 text-cyan-700', icon: FileCheck },
  paid: { label: 'Payé', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected: { label: 'Rejeté', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const PaymentRequestsPage = () => {
  const { user, token } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('creatives');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Data
  const [creativeRequests, setCreativeRequests] = useState([]);
  const [affiliateRequests, setAffiliateRequests] = useState([]);
  const [stats, setStats] = useState({});
  
  // Modals
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Payment form
  const [paymentForm, setPaymentForm] = useState({
    payment_method: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_reference: '',
    notes: ''
  });

  useEffect(() => {
    fetchPaymentRequests();
  }, []);

  const fetchPaymentRequests = async () => {
    try {
      const response = await axios.get(`${API}/payment-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCreativeRequests(response.data.creatives || []);
      setAffiliateRequests(response.data.affiliates || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching payment requests:', error);
      // Use mock data for demo
      const mockCreatives = [
        {
          id: 'pr-001',
          type: 'project', // project or monthly
          creative_id: 'creative-001',
          creative_name: 'Lucas Graphiste',
          creative_type: 'internal', // internal or external
          project_id: 'proj-001',
          project_name: 'Refonte site web ABC',
          client_name: 'Entreprise ABC',
          hours: 25,
          hourly_rate: 50,
          total_amount: 1250,
          status: 'ready_for_payment',
          agency_validated: true,
          agency_validated_at: '2024-12-20T10:00:00Z',
          client_validated: true,
          client_validated_at: '2024-12-21T14:00:00Z',
          survey_completed: true,
          survey_completed_at: '2024-12-22T09:00:00Z',
          invoice_uploaded: false,
          created_at: '2024-12-15T08:00:00Z'
        },
        {
          id: 'pr-002',
          type: 'monthly',
          creative_id: 'creative-002',
          creative_name: 'Sophie Design',
          creative_type: 'internal',
          month: '2024-12',
          total_hours: 140,
          hourly_rate: 50,
          total_amount: 7000,
          status: 'agency_validated',
          agency_validated: true,
          agency_validated_at: '2024-12-25T10:00:00Z',
          client_validated: false,
          projects_count: 5,
          created_at: '2024-12-01T08:00:00Z'
        },
        {
          id: 'pr-003',
          type: 'project',
          creative_id: 'creative-003',
          creative_name: 'Marc Freelance',
          creative_type: 'external',
          project_id: 'proj-002',
          project_name: 'Application mobile XYZ',
          client_name: 'Startup XYZ',
          hours: 80,
          hourly_rate: 45,
          total_amount: 3600,
          status: 'invoice_uploaded',
          agency_validated: true,
          client_validated: true,
          survey_completed: true,
          invoice_uploaded: true,
          invoice_url: '/uploads/invoices/invoice_pr003.pdf',
          created_at: '2024-12-10T08:00:00Z'
        },
        {
          id: 'pr-004',
          type: 'project',
          creative_id: 'creative-004',
          creative_name: 'Emma Motion',
          creative_type: 'external',
          project_id: 'proj-003',
          project_name: 'Vidéo corporate',
          client_name: 'Association Locale',
          hours: 15,
          hourly_rate: 55,
          total_amount: 825,
          status: 'pending_validation',
          agency_validated: false,
          client_validated: false,
          created_at: '2024-12-22T08:00:00Z'
        }
      ];
      
      const mockAffiliates = [
        {
          id: 'apr-001',
          affiliate_id: 'aff-001',
          affiliate_name: 'Jean Apporteur',
          leads_converted: 3,
          commission_rate: 10,
          total_sales: 15000,
          commission_amount: 1500,
          status: 'ready_for_payment',
          period: '2024-12',
          created_at: '2024-12-01T08:00:00Z'
        },
        {
          id: 'apr-002',
          affiliate_id: 'aff-002',
          affiliate_name: 'Marie Réseau',
          leads_converted: 1,
          commission_rate: 10,
          total_sales: 5000,
          commission_amount: 500,
          status: 'pending_validation',
          period: '2024-12',
          created_at: '2024-12-15T08:00:00Z'
        }
      ];
      
      setCreativeRequests(mockCreatives);
      setAffiliateRequests(mockAffiliates);
      setStats({
        creatives: {
          total: mockCreatives.length,
          pending: mockCreatives.filter(r => r.status === 'pending_validation').length,
          ready: mockCreatives.filter(r => r.status === 'ready_for_payment').length,
          total_amount: mockCreatives.reduce((sum, r) => sum + r.total_amount, 0)
        },
        affiliates: {
          total: mockAffiliates.length,
          pending: mockAffiliates.filter(r => r.status === 'pending_validation').length,
          ready: mockAffiliates.filter(r => r.status === 'ready_for_payment').length,
          total_amount: mockAffiliates.reduce((sum, r) => sum + r.commission_amount, 0)
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (request, validationType) => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/payment-requests/${request.id}/validate`,
        { type: validationType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Demande validée');
      fetchPaymentRequests();
      setShowDetailModal(false);
    } catch (error) {
      toast.error('Erreur lors de la validation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkAsPaid = async (request) => {
    // Check if invoice is required and uploaded
    if (request.type === 'project' && request.creative_type === 'external' && !request.invoice_uploaded) {
      toast.error('Le créatif doit d\'abord déposer sa facture');
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(
        `${API}/payment-requests/${request.id}/mark-paid`,
        {
          payment_method: paymentForm.payment_method,
          payment_date: paymentForm.payment_date,
          payment_reference: paymentForm.payment_reference,
          notes: paymentForm.notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Paiement enregistré !');
      fetchPaymentRequests();
      setShowPaymentModal(false);
      setShowDetailModal(false);
      setPaymentForm({ payment_method: '', payment_date: new Date().toISOString().split('T')[0], payment_reference: '', notes: '' });
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement du paiement');
    } finally {
      setActionLoading(false);
    }
  };

  const openPaymentModal = (request) => {
    setSelectedRequest(request);
    setShowPaymentModal(true);
  };

  const filterRequests = (requests) => {
    return requests.filter(r => {
      const matchesStatus = filterStatus === 'all' || r.status === filterStatus;
      const matchesSearch = !searchQuery || 
        r.creative_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.affiliate_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.project_name?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesStatus && matchesSearch;
    });
  };

  const filteredCreatives = filterRequests(creativeRequests);
  const filteredAffiliates = filterRequests(affiliateRequests);
  const currentStats = activeTab === 'creatives' ? stats.creatives : stats.affiliates;

  const renderWorkflowStatus = (request) => {
    const steps = [
      { key: 'agency', label: 'Agence', done: request.agency_validated },
      { key: 'client', label: 'Client', done: request.client_validated },
      { key: 'survey', label: 'Enquête', done: request.survey_completed, optional: true },
    ];
    
    if (request.type === 'project' && request.creative_type === 'external') {
      steps.push({ key: 'invoice', label: 'Facture', done: request.invoice_uploaded });
    }
    
    return (
      <div className="flex items-center gap-1 flex-wrap">
        {steps.map((step, idx) => (
          <React.Fragment key={step.key}>
            <div className={cn(
              "flex items-center gap-1 px-2 py-1 rounded-full text-xs",
              step.done 
                ? "bg-green-100 text-green-700" 
                : step.optional 
                  ? "bg-slate-100 text-slate-400"
                  : "bg-amber-100 text-amber-700"
            )}>
              {step.done ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
              {step.label}
              {step.optional && !step.done && <span className="text-[10px]">(opt)</span>}
            </div>
            {idx < steps.length - 1 && <ChevronRight className="w-3 h-3 text-slate-300" />}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderCreativeCard = (request) => {
    const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending_validation;
    const StatusIcon = statusConfig.icon;
    const isExternal = request.creative_type === 'external';
    const isMonthly = request.type === 'monthly';
    
    return (
      <Card key={request.id} className="hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className={cn(
                  "w-12 h-12 rounded-xl flex items-center justify-center shrink-0",
                  isExternal ? "bg-purple-100" : "bg-cyan-100"
                )}>
                  <User className={cn("w-6 h-6", isExternal ? "text-purple-600" : "text-cyan-600")} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-800">{request.creative_name}</h3>
                    <Badge variant="outline" className="text-xs">
                      {isExternal ? 'Ponctuel' : 'Intégré'}
                    </Badge>
                    <Badge className={cn("text-xs", isMonthly ? "bg-violet-100 text-violet-700" : "bg-blue-100 text-blue-700")}>
                      {isMonthly ? 'Mensuel' : 'Projet'}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-500">
                    {isMonthly 
                      ? `Rapport ${new Date(request.month + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })} • ${request.projects_count || 0} projets`
                      : `${request.project_name} • ${request.client_name}`
                    }
                  </p>
                </div>
              </div>
              <Badge className={statusConfig.color}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {statusConfig.label}
              </Badge>
            </div>
            
            {/* Workflow status */}
            <div>
              {renderWorkflowStatus(request)}
            </div>
            
            {/* Details */}
            <div className="flex items-center justify-between pt-2 border-t">
              <div className="flex items-center gap-6 text-sm">
                <span className="flex items-center gap-1 text-slate-600">
                  <Clock className="w-4 h-4" />
                  {request.hours || request.total_hours}h
                </span>
                <span className="text-slate-400">×</span>
                <span className="text-slate-600">{request.hourly_rate}€/h</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-xl font-bold text-slate-800">
                    {request.total_amount.toLocaleString('fr-FR')}€
                  </p>
                  <p className="text-xs text-slate-500">HT</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedRequest(request);
                    setShowDetailModal(true);
                  }}
                  className="rounded-full"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Détails
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderAffiliateCard = (request) => {
    const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending_validation;
    const StatusIcon = statusConfig.icon;
    
    return (
      <Card key={request.id} className="hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center shrink-0">
                <HandCoins className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-slate-800">{request.affiliate_name}</h3>
                  <Badge className={statusConfig.color}>
                    <StatusIcon className="w-3 h-3 mr-1" />
                    {statusConfig.label}
                  </Badge>
                </div>
                <p className="text-sm text-slate-500">
                  Période: {new Date(request.period + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  {request.leads_converted} lead(s) converti(s) • {request.commission_rate}% commission
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-xs text-slate-500">CA généré</p>
                <p className="font-semibold">{request.total_sales?.toLocaleString('fr-FR')}€</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">Commission</p>
                <p className="text-xl font-bold text-orange-600">
                  {request.commission_amount?.toLocaleString('fr-FR')}€
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedRequest(request);
                  setShowDetailModal(true);
                }}
                className="rounded-full"
              >
                <Eye className="w-4 h-4 mr-1" />
                Détails
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      
      <div className={cn(
        "transition-all duration-300",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
        
        <main className="px-4 md:px-8 pb-6 md:pb-8 pt-24">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
                Demandes de paiement
              </h1>
              <p className="text-slate-500 mt-1">
                Gérez les demandes de paiement des créatifs et apporteurs d'affaires
              </p>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-2 max-w-md">
              <TabsTrigger value="creatives" className="gap-2">
                <User className="w-4 h-4" />
                Créatifs
                {stats.creatives?.pending > 0 && (
                  <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                    {stats.creatives.pending}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="affiliates" className="gap-2">
                <HandCoins className="w-4 h-4" />
                Apporteurs
                {stats.affiliates?.pending > 0 && (
                  <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                    {stats.affiliates.pending}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{currentStats?.total || 0}</p>
                      <p className="text-xs text-slate-500">Total demandes</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{currentStats?.pending || 0}</p>
                      <p className="text-xs text-slate-500">En attente</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{currentStats?.ready || 0}</p>
                      <p className="text-xs text-slate-500">Prêt à payer</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <Euro className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{(currentStats?.total_amount || 0).toLocaleString('fr-FR')}€</p>
                      <p className="text-xs text-slate-500">Montant total</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Filters */}
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input 
                      placeholder="Rechercher..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-full md:w-[200px]">
                      <SelectValue placeholder="Statut" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les statuts</SelectItem>
                      <SelectItem value="pending_validation">En attente validation</SelectItem>
                      <SelectItem value="agency_validated">Validé agence</SelectItem>
                      <SelectItem value="ready_for_payment">Prêt pour paiement</SelectItem>
                      <SelectItem value="invoice_uploaded">Facture déposée</SelectItem>
                      <SelectItem value="paid">Payé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Creatives Tab */}
            <TabsContent value="creatives" className="space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : filteredCreatives.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <User className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">Aucune demande</h3>
                    <p className="text-slate-500">Les demandes de paiement des créatifs apparaîtront ici</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {filteredCreatives.map(renderCreativeCard)}
                </div>
              )}
            </TabsContent>

            {/* Affiliates Tab */}
            <TabsContent value="affiliates" className="space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : filteredAffiliates.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <HandCoins className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">Aucune demande</h3>
                    <p className="text-slate-500">Les demandes de paiement des apporteurs apparaîtront ici</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {filteredAffiliates.map(renderAffiliateCard)}
                </div>
              )}
            </TabsContent>
          </Tabs>

          {/* Workflow explanation */}
          <Card className="mt-8">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-blue-500" />
                Processus de validation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <User className="w-4 h-4 text-cyan-600" />
                    Créatifs intégrés (mensuels)
                  </h4>
                  <ol className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center text-xs shrink-0">1</span>
                      <span>Rapport mensuel généré automatiquement</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center text-xs shrink-0">2</span>
                      <span>Validation par l'agence</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center text-xs shrink-0">3</span>
                      <span>Paiement en fin de mois</span>
                    </li>
                  </ol>
                </div>
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <User className="w-4 h-4 text-purple-600" />
                    Créatifs ponctuels (par projet)
                  </h4>
                  <ol className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs shrink-0">1</span>
                      <span>Projet validé par l'agence</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs shrink-0">2</span>
                      <span>Projet validé par le client</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs shrink-0">3</span>
                      <span>Enquête de satisfaction complétée (optionnel)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs shrink-0">4</span>
                      <span><strong>Dépôt de facture</strong> par le créatif (débloqué)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs shrink-0">5</span>
                      <span>Paiement</span>
                    </li>
                  </ol>
                </div>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              Détails de la demande
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              {/* Info */}
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="font-semibold text-lg">{selectedRequest.creative_name || selectedRequest.affiliate_name}</p>
                    <p className="text-sm text-slate-500">
                      {selectedRequest.type === 'monthly' 
                        ? `Rapport mensuel - ${selectedRequest.projects_count || 0} projets`
                        : selectedRequest.project_name || `Période ${selectedRequest.period}`
                      }
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">
                      {(selectedRequest.total_amount || selectedRequest.commission_amount)?.toLocaleString('fr-FR')}€
                    </p>
                    <p className="text-xs text-slate-500">HT</p>
                  </div>
                </div>
                
                {selectedRequest.hours && (
                  <div className="flex items-center gap-4 text-sm border-t pt-3">
                    <span><strong>{selectedRequest.hours || selectedRequest.total_hours}h</strong> travaillées</span>
                    <span>×</span>
                    <span><strong>{selectedRequest.hourly_rate}€</strong>/h</span>
                  </div>
                )}
              </div>
              
              {/* Workflow status */}
              {selectedRequest.creative_name && (
                <div>
                  <Label className="text-xs text-slate-500">Étapes de validation</Label>
                  <div className="mt-2">
                    {renderWorkflowStatus(selectedRequest)}
                  </div>
                </div>
              )}
              
              {/* Invoice upload status for external creatives */}
              {selectedRequest.creative_type === 'external' && selectedRequest.type === 'project' && (
                <div className={cn(
                  "p-4 rounded-xl border-2",
                  selectedRequest.invoice_uploaded 
                    ? "bg-emerald-50 border-emerald-200"
                    : selectedRequest.status === 'ready_for_payment'
                      ? "bg-amber-50 border-amber-200"
                      : "bg-slate-50 border-slate-200"
                )}>
                  <div className="flex items-center gap-3">
                    <FileCheck className={cn(
                      "w-8 h-8",
                      selectedRequest.invoice_uploaded ? "text-emerald-500" : "text-slate-400"
                    )} />
                    <div className="flex-1">
                      <p className="font-medium">
                        {selectedRequest.invoice_uploaded 
                          ? "Facture déposée"
                          : "En attente de facture"
                        }
                      </p>
                      <p className="text-xs text-slate-500">
                        {selectedRequest.invoice_uploaded 
                          ? "Le créatif a déposé sa facture"
                          : selectedRequest.status === 'ready_for_payment'
                            ? "Le créatif peut maintenant déposer sa facture"
                            : "Le dépôt sera débloqué après validation complète"
                        }
                      </p>
                    </div>
                    {selectedRequest.invoice_uploaded && selectedRequest.invoice_url && (
                      <Button variant="outline" size="sm" asChild>
                        <a href={`${API}${selectedRequest.invoice_url}`} target="_blank" rel="noreferrer" className="gap-1">
                          <Download className="w-4 h-4" />
                          Voir
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Link to activity report */}
              {selectedRequest.creative_id && (
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="flex items-center gap-2 text-sm text-blue-700">
                    <FileText className="w-4 h-4" />
                    <span>Rapport d'activité du créatif</span>
                  </div>
                  <Button variant="outline" size="sm" className="text-blue-700 border-blue-200 hover:bg-blue-100">
                    <Eye className="w-4 h-4 mr-1" />
                    Consulter
                  </Button>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-wrap gap-2">
            {selectedRequest?.status === 'pending_validation' && (
              <Button
                onClick={() => handleValidate(selectedRequest, 'agency')}
                disabled={actionLoading}
                className="gap-2"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Valider (Agence)
              </Button>
            )}
            
            {(selectedRequest?.status === 'ready_for_payment' || selectedRequest?.status === 'invoice_uploaded') && (
              <Button
                onClick={() => openPaymentModal(selectedRequest)}
                disabled={actionLoading || (selectedRequest?.creative_type === 'external' && selectedRequest?.type === 'project' && !selectedRequest?.invoice_uploaded)}
                className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                Marquer comme payé
              </Button>
            )}
            
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-600" />
              Enregistrer le paiement
            </DialogTitle>
            <DialogDescription>
              Confirmez les détails du paiement pour {selectedRequest?.creative_name || selectedRequest?.affiliate_name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              {/* Amount summary */}
              <div className="bg-emerald-50 rounded-xl p-4 text-center">
                <p className="text-sm text-emerald-700 mb-1">Montant à payer</p>
                <p className="text-3xl font-bold text-emerald-700">
                  {(selectedRequest.total_amount || selectedRequest.commission_amount)?.toLocaleString('fr-FR')}€
                </p>
                <p className="text-xs text-emerald-600">HT</p>
              </div>
              
              {/* Payment form */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="payment_method">Mode de paiement *</Label>
                  <Select 
                    value={paymentForm.payment_method} 
                    onValueChange={(value) => setPaymentForm({...paymentForm, payment_method: value})}
                  >
                    <SelectTrigger id="payment_method">
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="virement">Virement bancaire</SelectItem>
                      <SelectItem value="cheque">Chèque</SelectItem>
                      <SelectItem value="especes">Espèces</SelectItem>
                      <SelectItem value="carte">Carte bancaire</SelectItem>
                      <SelectItem value="paypal">PayPal</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="payment_date">Date du paiement *</Label>
                  <Input 
                    id="payment_date"
                    type="date"
                    value={paymentForm.payment_date}
                    onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="payment_reference">Référence (optionnel)</Label>
                  <Input 
                    id="payment_reference"
                    placeholder="Ex: VIR-2024-001, Chèque n°..."
                    value={paymentForm.payment_reference}
                    onChange={(e) => setPaymentForm({...paymentForm, payment_reference: e.target.value})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="payment_notes">Notes (optionnel)</Label>
                  <Textarea 
                    id="payment_notes"
                    placeholder="Commentaires additionnels..."
                    rows={2}
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                  />
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowPaymentModal(false);
                setPaymentForm({ payment_method: '', payment_date: new Date().toISOString().split('T')[0], payment_reference: '', notes: '' });
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={() => handleMarkAsPaid(selectedRequest)}
              disabled={actionLoading || !paymentForm.payment_method}
              className="gap-2 bg-emerald-600 hover:bg-emerald-700"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              Confirmer le paiement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentRequestsPage;
